import { aed, pctLabel, ROOM_KIND_LABEL, type PublicOrderView, type PublicSection } from "@/lib/orders";

function Section({ title, s }: { title: string; s: PublicSection | null }) {
  if (!s) return null;
  return (
    <div className="glass-panel-strong p-4">
      <div className="brand-kicker">{title}</div>
      <div className="mt-1 font-serif text-xl text-paper">{s.finish}</div>
      <div className="mt-1 text-sm text-ice/80">
        Colour <b className="text-paper">{s.colour}</b> ·{" "}
        {s.packaged ? <>package AED <span className="num">{aed(s.unitPrice)}</span>{s.sqm > 0 && <> · approx. <span className="num">{s.sqm}</span> m²</>}</> : <><span className="num">{s.sqm}</span> m² × AED <span className="num">{aed(s.unitPrice)}</span></>}
      </div>
      {s.extras.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.extras.map((e) => (
            <span key={e.label} className="inline-flex items-center gap-1.5 rounded-full border border-gold/60 bg-gold/15 px-2.5 py-0.5 text-xs text-goldbright">
              {e.label} <b>{e.value}</b>
            </span>
          ))}
        </div>
      )}
      <div className="mt-2 flex justify-between border-t border-white/10 pt-2 text-sm">
        <span className="text-ice/70">{title} total</span>
        <b className="num text-paper">AED {aed(s.total)}</b>
      </div>
    </div>
  );
}

/** The customer-facing summary: what is ordered, what it costs, the terms. */
export function OrderSummaryView({ view, pdfUrl }: { view: PublicOrderView; pdfUrl: string }) {
  return (
    <div className="space-y-3">
      {view.rooms.map((r, i) => (
        <div key={i} className="space-y-2">
          <div className="flex items-baseline justify-between px-1">
            <div>
              <span className="font-serif text-lg text-paper">{r.name}</span>
              <span className="ml-2 text-[0.62rem] uppercase tracking-[0.16em] text-ice/50">{ROOM_KIND_LABEL[r.kind]}</span>
            </div>
            <b className="num text-sm text-goldbright">AED {aed(r.total)}</b>
          </div>
          <Section title="Floor" s={r.floor} />
          <Section title="Wall" s={r.wall} />
        </div>
      ))}

      <div className="glass-panel-strong p-4 text-sm">
        {view.bracketPct !== 100 && (
          <div className="flex justify-between py-1 text-ice/80">
            <span>Quantity bracket</span>
            <b className="text-paper">{pctLabel(view.bracketPct)}</b>
          </div>
        )}
        {view.discountAmt > 0 && (
          <div className="flex justify-between py-1 text-ice/80">
            <span>Discount {view.discountLabel}</span>
            <b className="num text-paper">− {aed(view.discountAmt)}</b>
          </div>
        )}
        <div className="flex justify-between py-1 text-ice/80">
          <span>Net</span>
          <b className="num text-paper">{aed(view.net)}</b>
        </div>
        <div className="flex justify-between py-1 text-ice/80">
          <span>VAT {view.vatPct}%</span>
          <b className="num text-paper">{aed(view.vatAmt)}</b>
        </div>
        <div className="mt-2 flex items-baseline justify-between rounded-xl bg-night/70 px-4 py-3">
          <span className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-goldbright">Total AED</span>
          <span className="num font-serif text-3xl text-goldbright">{aed(view.total)}</span>
        </div>
        <div className="mt-2 flex justify-between text-ice/80">
          <span>Deposit {view.depositPct}% now</span>
          <b className="num text-paper">{aed(view.depositAmt)}</b>
        </div>
        <div className="flex justify-between text-ice/80">
          <span>Balance on completion</span>
          <b className="num text-paper">{aed(view.total - view.depositAmt)}</b>
        </div>
      </div>

      {view.payment && (
        <div className="glass-panel p-4 text-sm text-ice/80">
          <div className="brand-kicker mb-1">Payment</div>
          <p className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{view.payment}</p>
        </div>
      )}

      {view.terms && (
        <details className="glass-panel p-4 text-sm text-ice/80">
          <summary className="cursor-pointer text-paper">Terms</summary>
          <p className="mt-2 whitespace-pre-wrap leading-relaxed">{view.terms}</p>
        </details>
      )}

      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="block text-center text-sm text-pink underline underline-offset-4">
        View the full PDF
      </a>
    </div>
  );
}
